"use client";

import * as React from "react";

import axios from "axios";
import { cn } from "lib/utils";
import { MapPin } from "lucide-react";

import type { AddressFormData } from "@/types/address";
import { findBestColoniaMatch } from "@/utils/coloniasMatcher";
import { normalizeStateName } from "@/utils/normalizeAddress";

import { ColoniaCombobox } from "js/components/ui/colonia-combobox";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";

import { useGetZipCode } from "../../../hooks/useZipCodes";
import { toast } from "../../../utils/toast";

export interface AddressFormSepomexProps {
  /**
   * Current address form data
   */
  value: AddressFormData;
  /**
   * Callback when any field changes
   */
  onChange: (data: AddressFormData) => void;
  /**
   * Callback when a field loses focus
   */
  onBlur?: (field: keyof AddressFormData) => void;
  /**
   * Field validation errors
   */
  errors?: Partial<Record<keyof AddressFormData, string>>;
  /**
   * Fields that have been touched
   */
  touched?: Partial<Record<keyof AddressFormData, boolean>>;
  /**
   * Whether the form is disabled
   */
  disabled?: boolean;
  /**
   * Whether data is loading
   */
  isLoading?: boolean;
  /**
   * Section title
   */
  title?: string;
  /**
   * Show section header
   */
  showHeader?: boolean;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Field configuration for character limits
   */
  fieldLimits?: Partial<Record<keyof AddressFormData, number>>;
  /**
   * Transform function for field values (e.g., uppercase)
   */
  transformValue?: (value: string) => string;
}

// Limits homologated to Buró de Crédito (the most restrictive provider).
// CDC accepts these or longer values, so a single set of limits keeps
// both providers happy and the form stays simple.
//
// Address split note: BDC exposes two address fields per person
// (direccion1+direccion2 for PF, direccion+segundaDireccion for PM),
// each ≤ 40 chars. mb-sic-api concatenates `street + ext + int` and
// splits the result across both fields automatically, so the effective
// limit for the user is 80 characters (40+40). `street` therefore
// allows up to 58 chars, leaving ' <ext> <int>' (up to 22 chars) room
// to fit within 80.
const BDC_MAX_DOMICILIO_LENGTH = 40;
const DEFAULT_FIELD_LIMITS: Partial<Record<keyof AddressFormData, number>> = {
  street: 58,
  exterior_number: 10,
  interior_number: 10,
  neighborhood: BDC_MAX_DOMICILIO_LENGTH,
  city: BDC_MAX_DOMICILIO_LENGTH,
  municipality: BDC_MAX_DOMICILIO_LENGTH,
  state: 40,
  zip_code: 5,
};

export const getZipCodeLookupErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return "El código postal no existe.";
  }

  return "No se pudo consultar el código postal. Inténtalo de nuevo.";
};

/**
 * AddressFormSepomex Component
 *
 * Legacy address entry form used ONLY by the Express Report ("autorización
 * de buró") flow. Matches the pre-Google-Places behavior exactly:
 *   - CP drives the SEPOMEX lookup.
 *   - Colonia is a fixed-option combobox fed from the SEPOMEX response.
 *   - Ciudad is editable (the user may want to correct it).
 *   - Municipio is hard-coded disabled — the SEPOMEX value is authoritative.
 *   - Estado is hard-coded disabled — the Select only exposes the state
 *     returned by SEPOMEX.
 *   - If SEPOMEX can't resolve the CP we show a toast (no manual mode).
 *
 * Do not use this in new consumers — prefer the shared AddressForm
 * component (which does include Google Places) instead.
 */
export function AddressFormSepomex({
  value,
  onChange,
  onBlur,
  errors = {},
  touched = {},
  disabled = false,
  isLoading = false,
  title = "Domicilio",
  showHeader = true,
  className,
  fieldLimits = {},
  transformValue = (v) =>
    v
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\./g, ""),
}: AddressFormSepomexProps) {
  const limits = { ...DEFAULT_FIELD_LIMITS, ...fieldLimits };

  // ZIP code lookup
  const isValidZipCode = /^\d{5}$/.test(value.zip_code);
  const {
    data: zipCodeData,
    isLoading: isLoadingZipCode,
    isError: isZipCodeError,
    error: zipCodeError,
  } = useGetZipCode(value.zip_code, {
    enabled: isValidZipCode,
  });

  // Colonia options come straight from SEPOMEX
  const coloniaOptions = React.useMemo(() => {
    const sepomexColonias = zipCodeData?.colonias ?? [];
    return sepomexColonias.map((c) => ({ value: c, label: c }));
  }, [zipCodeData?.colonias]);
  const allowManualNeighborhoodEntry =
    !!zipCodeData && coloniaOptions.length === 0;

  // Update form when ZIP code data is received
  React.useEffect(() => {
    if (zipCodeData && isValidZipCode) {
      const { ciudad, estado, municipio, colonias } = zipCodeData;

      // Check if the already-typed colonia is still valid for the new CP.
      // (In practice it will be empty because the zip_code onChange
      // handler clears locality whenever the CP changes, but the check
      // keeps us safe in edge cases like typing the same CP twice.)
      const currentNeighborhood = value.neighborhood;
      const neighborhoodStillValid =
        currentNeighborhood &&
        colonias?.length > 0 &&
        findBestColoniaMatch(currentNeighborhood, colonias, 0.8) !== null;

      const rawCity = ciudad ? transformValue(ciudad) : value.city;
      const rawMunicipality = municipio
        ? transformValue(municipio)
        : value.municipality;

      // Buró de Crédito rejects domicilio fields > 40 chars (error PAPA41).
      // SEPOMEX occasionally returns long names (e.g. "DOLORES HIDALGO
      // CUNA DE LA INDEPENDENCIA NACIONAL" = 49 chars). The inputs are
      // disabled in this form, so the user cannot fix the value manually.
      // Truncate to BDC_MAX_DOMICILIO_LENGTH and tell the user via toast,
      // which is strictly better than letting the request fail downstream.
      const transformedCity = rawCity.slice(0, BDC_MAX_DOMICILIO_LENGTH);
      const transformedMunicipality = rawMunicipality.slice(
        0,
        BDC_MAX_DOMICILIO_LENGTH,
      );

      if (rawCity.length > BDC_MAX_DOMICILIO_LENGTH) {
        toast.info(
          `La ciudad "${rawCity}" (${rawCity.length} caracteres) fue recortada a "${transformedCity}" para cumplir el límite de Buró de Crédito (${BDC_MAX_DOMICILIO_LENGTH}).`,
        );
      }
      if (rawMunicipality.length > BDC_MAX_DOMICILIO_LENGTH) {
        toast.info(
          `El municipio "${rawMunicipality}" (${rawMunicipality.length} caracteres) fue recortado a "${transformedMunicipality}" para cumplir el límite de Buró de Crédito (${BDC_MAX_DOMICILIO_LENGTH}).`,
        );
      }

      onChange({
        ...value,
        city: transformedCity,
        state: estado ? normalizeStateName(estado) : value.state,
        municipality: transformedMunicipality,
        neighborhood: neighborhoodStillValid ? value.neighborhood : "",
      });
    }
    // We intentionally only re-run this effect when the SEPOMEX response
    // (zipCodeData) or the CP validity flag change. Including `value`,
    // `onChange`, or `transformValue` in the deps would retrigger on every
    // keystroke and cause an infinite update loop (this effect calls
    // onChange, which produces a new `value`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCodeData, isValidZipCode]);

  // Mirror the pre-Google-Places UX: on SEPOMEX error, show a toast. There
  // is no manual-entry fallback — the user must correct the CP.
  React.useEffect(() => {
    if (isZipCodeError && isValidZipCode) {
      toast.error(getZipCodeLookupErrorMessage(zipCodeError));
    }
  }, [isValidZipCode, isZipCodeError, zipCodeError]);

  // Update field helper
  const updateField = <K extends keyof AddressFormData>(
    field: K,
    fieldValue: AddressFormData[K],
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Section Header */}
      {showHeader && (
        <div className="flex items-center gap-2 pb-2">
          <MapPin className="h-4 w-4 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
      )}

      {/* Street, Exterior Number, Interior Number */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Calle */}
        <div className="space-y-2">
          <Label htmlFor="street" className="text-sm font-medium text-gray-700">
            Calle *
          </Label>
          <Input
            id="street"
            type="text"
            value={value.street}
            onChange={(e) =>
              updateField("street", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("street")}
            maxLength={limits.street}
            disabled={disabled || isLoading}
            placeholder="Calle"
            autoComplete="street-address"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.street && errors.street && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.street.length}/{limits.street}
            </p>
            {touched.street && errors.street && (
              <p className="text-xs text-red-600">{errors.street}</p>
            )}
          </div>
        </div>

        {/* Número Exterior */}
        <div className="space-y-2">
          <Label
            htmlFor="exterior_number"
            className="text-sm font-medium text-gray-700"
          >
            Número Exterior *
          </Label>
          <Input
            id="exterior_number"
            type="text"
            value={value.exterior_number}
            onChange={(e) =>
              updateField("exterior_number", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("exterior_number")}
            maxLength={limits.exterior_number}
            disabled={disabled || isLoading}
            placeholder="Número Exterior"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.exterior_number &&
                errors.exterior_number &&
                "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.exterior_number.length}/{limits.exterior_number}
            </p>
            {touched.exterior_number && errors.exterior_number && (
              <p className="text-xs text-red-600">{errors.exterior_number}</p>
            )}
          </div>
        </div>

        {/* Número Interior */}
        <div className="space-y-2">
          <Label
            htmlFor="interior_number"
            className="text-sm font-medium text-gray-700"
          >
            Número Interior (opcional)
          </Label>
          <Input
            id="interior_number"
            type="text"
            value={value.interior_number}
            onChange={(e) =>
              updateField("interior_number", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("interior_number")}
            maxLength={limits.interior_number}
            disabled={disabled || isLoading}
            placeholder="Número Interior"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.interior_number &&
                errors.interior_number &&
                "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.interior_number.length}/{limits.interior_number}
            </p>
            {touched.interior_number && errors.interior_number && (
              <p className="text-xs text-red-600">{errors.interior_number}</p>
            )}
          </div>
        </div>
      </div>

      {/* ZIP Code, Colonia, City, Municipality, State */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        {/* Código Postal */}
        <div className="space-y-2">
          <Label
            htmlFor="zip_code"
            className="text-sm font-medium text-gray-700"
          >
            Código Postal *
          </Label>
          <Input
            id="zip_code"
            type="text"
            value={value.zip_code}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/[^0-9]/g, "");
              if (numericValue === value.zip_code) return;
              // When the CP changes, clear any previously auto-filled
              // locality so the form can't submit the previous city /
              // municipality / state / colonia while the new SEPOMEX
              // response is still in flight.
              onChange({
                ...value,
                zip_code: numericValue,
                city: "",
                municipality: "",
                state: "",
                neighborhood: "",
              });
            }}
            onBlur={() => onBlur?.("zip_code")}
            maxLength={limits.zip_code}
            disabled={disabled || isLoading}
            placeholder="Código Postal"
            autoComplete="postal-code"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.zip_code && errors.zip_code && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.zip_code.length}/{limits.zip_code}
            </p>
            {touched.zip_code && errors.zip_code && (
              <p className="text-xs text-red-600">{errors.zip_code}</p>
            )}
          </div>
        </div>

        {/* Colonia */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Colonia *</Label>
          <ColoniaCombobox
            value={value.neighborhood}
            onChange={(newValue) =>
              updateField(
                "neighborhood",
                newValue ? transformValue(newValue) : newValue,
              )
            }
            onBlur={() => onBlur?.("neighborhood")}
            options={coloniaOptions}
            placeholder="Colonia"
            disabled={disabled || isLoading}
            isLoading={isLoadingZipCode}
            allowCustomValue={allowManualNeighborhoodEntry}
            error={
              touched.neighborhood && errors.neighborhood
                ? errors.neighborhood
                : undefined
            }
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.neighborhood && errors.neighborhood && "border-red-500",
            )}
          />
          {touched.neighborhood && errors.neighborhood && (
            <p className="text-xs text-red-600">{errors.neighborhood}</p>
          )}
        </div>

        {/* Ciudad — editable (the user may correct SEPOMEX's value) */}
        <div className="space-y-2">
          <Label htmlFor="city" className="text-sm font-medium text-gray-700">
            Ciudad *
          </Label>
          <Input
            id="city"
            type="text"
            value={value.city}
            onChange={(e) =>
              updateField("city", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("city")}
            maxLength={limits.city}
            disabled={disabled || isLoading}
            placeholder="Ciudad"
            autoComplete="address-level2"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.city && errors.city && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.city.length}/{limits.city}
            </p>
            {touched.city && errors.city && (
              <p className="text-xs text-red-600">{errors.city}</p>
            )}
          </div>
        </div>

        {/* Municipio — always disabled; value comes from SEPOMEX */}
        <div className="space-y-2">
          <Label
            htmlFor="municipality"
            className="text-sm font-medium text-gray-700"
          >
            Municipio *
          </Label>
          <Input
            id="municipality"
            type="text"
            value={value.municipality}
            onBlur={() => onBlur?.("municipality")}
            disabled
            placeholder="Municipio"
            autoComplete="address-level1"
            className={cn(
              "border-gray-200 disabled:cursor-not-allowed disabled:opacity-50",
              touched.municipality && errors.municipality && "border-red-500",
            )}
          />
          {touched.municipality && errors.municipality && (
            <p className="text-xs text-red-600">{errors.municipality}</p>
          )}
        </div>

        {/* Estado — always disabled; Select only exposes the SEPOMEX value */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Estado *</Label>
          <Select value={value.state} disabled>
            <SelectTrigger
              aria-label="Estado"
              className={cn(
                "border-gray-200 disabled:cursor-not-allowed disabled:opacity-50",
                touched.state && errors.state && "border-red-500",
              )}
            >
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {value.state && (
                <SelectItem value={value.state}>{value.state}</SelectItem>
              )}
            </SelectContent>
          </Select>
          {touched.state && errors.state && (
            <p className="text-xs text-red-600">{errors.state}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddressFormSepomex;
